import { Controller } from '@hotwired/stimulus';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// One fixed colour per feature, so a feature keeps its colour across both charts and both ranges.
// Eight hues is the ceiling for a categorical palette; the two smallest features share "Other".
// A 1px surface border separates stacked segments; past 45 bars it would only speckle them.
const SERIES = [
  { key: 'progress_tracking', color: '#2a78d6' },
  { key: 'ai_scribe_web', color: '#eb6834' },
  { key: 'ai_scribe_mobile', color: '#1baf7a' },
  { key: 'parent_summary', color: '#eda100' },
  { key: 'session_planning', color: '#e87ba4' },
  { key: 'kiki_creates', color: '#008300' },
  { key: 'tenalog', color: '#6250d6' },
  { key: 'ai_help', color: '#e34948' },
];
const OTHER = { key: 'other', label: 'Other', color: '#adb5bd' };
const SURFACE = '#ffffff';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

// The admin AI usage dashboard's trend charts. `daily` is Cacher::AdminAiUsageDashboard's daily
// rows: one per UTC day, oldest first, with uses and cost cents per feature key. `labels` maps
// feature keys to their display names.
export default class extends Controller {
  static values = { daily: Array, labels: Object, days: { type: Number, default: 30 } };

  static targets = ['usesChart', 'costChart', 'rangeButton'];

  connect() {
    this.charts = [];
    this.render();
  }

  disconnect() {
    this.destroy();
  }

  range(event) {
    this.daysValue = Number(event.currentTarget.dataset.days);
    this.rangeButtonTargets.forEach((button) => {
      button.classList.toggle('active', Number(button.dataset.days) === this.daysValue);
    });
  }

  daysValueChanged() {
    if (this.charts) this.render();
  }

  destroy() {
    this.charts.forEach((chart) => chart.destroy());
    this.charts = [];
  }

  render() {
    this.destroy();
    const points = this.dailyValue.slice(-this.daysValue);
    const labels = points.map((point) => point.date.slice(5).replace('-', '/'));

    this.charts.push(
      this.stackedBars(
        this.usesChartTarget,
        labels,
        this.datasets(points, 'uses', (value) => value),
        {
          ticks: { precision: 0, callback: (value) => compact.format(value) },
          title: { display: true, text: 'Uses' },
        },
        (value) => value.toLocaleString('en-US')
      )
    );
    this.charts.push(
      this.stackedBars(
        this.costChartTarget,
        labels,
        this.datasets(points, 'cost_cents', (cents) => cents / 100),
        {
          ticks: { callback: (value) => currency.format(value) },
          title: { display: true, text: 'Estimated cost (USD)' },
        },
        (value) => currency.format(value)
      )
    );
  }

  // One dataset per series, the features outside SERIES folded into "Other". Series with no
  // value in the range are left out so the legend lists only what is on the chart.
  datasets(points, field, convert) {
    const keys = Object.keys(this.labelsValue);
    const named = SERIES.filter((series) => keys.includes(series.key));
    const otherKeys = keys.filter((key) => !SERIES.some((series) => series.key === key));
    const rows = named.map((series) => ({
      label: this.labelsValue[series.key],
      data: points.map((point) => convert(point[field][series.key] || 0)),
      backgroundColor: series.color,
    }));
    if (otherKeys.length > 0) {
      rows.push({
        label: OTHER.label,
        data: points.map((point) => convert(otherKeys.reduce((sum, key) => sum + (point[field][key] || 0), 0))),
        backgroundColor: OTHER.color,
      });
    }
    return rows
      .filter((row) => row.data.some((value) => value > 0))
      .map((row) => ({ ...row, borderColor: SURFACE, borderWidth: this.daysValue > 45 ? 0 : 1, maxBarThickness: 24 }));
  }

  stackedBars(canvas, labels, datasets, yScale, formatValue) {
    return new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (item) => `${item.dataset.label}: ${formatValue(item.parsed.y)}`,
              footer: (items) => `Total: ${formatValue(items.reduce((sum, item) => sum + item.parsed.y, 0))}`,
            },
          },
        },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { maxTicksLimit: 15, maxRotation: 0 } },
          y: { stacked: true, beginAtZero: true, ...yScale },
        },
      },
    });
  }
}
